import { useEffect, useState } from "react";
import { BadgeCheck, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useSpace } from "@/context/SpaceContext";
import { translateError } from "@/i18n/errors";

const FIELDS = ["legal_name", "contact_email", "country", "console_id"];
const STATUSES = ["not_started", "in_progress", "verified"];

// Google "Android developer verification" info + per-owner pre-fill form (tenant-scoped).
export const AndroidVerificationCard = () => {
  const { t } = useI18n();
  const { base } = useSpace();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`${base}/android-verification`).then(({ data }) => setForm(data)).catch(() => setForm(null)); }, [base]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put(`${base}/android-verification`, Object.fromEntries([...FIELDS, "status"].map((k) => [k, form[k] || ""])));
      setForm(data);
      toast.success(t("verify.saved"));
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card-carbon min-w-0 rounded-2xl p-5" data-testid="android-verification-card">
      <h3 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white"><ShieldCheck className="h-5 w-5 text-[#3fd6e0]" /> {t("verify.title")}</h3>
      <p className="mt-2 text-xs leading-relaxed text-neutral-300">{t("verify.info")}</p>
      <a href="https://developer.android.com/developer-verification" target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-xs text-[var(--gold)] underline" data-testid="verify-link">{t("verify.learn")}</a>
      {form && (
        <form onSubmit={save} className="mt-4 space-y-2">
          {FIELDS.map((k) => (
            <Input key={k} value={form[k] || ""} placeholder={t(`verify.${k}`)} aria-label={t(`verify.${k}`)} maxLength={200}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`verify-${k}-input`} />
          ))}
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} aria-label={t("verify.status")}
            className="w-full rounded-md border border-neutral-700 bg-black/40 px-3 py-2 text-sm text-white" data-testid="verify-status-select">
            {STATUSES.map((s) => <option key={s} value={s}>{t(`verify.s.${s}`)}</option>)}
          </select>
          {form.admin_confirmed && <p className="flex items-center gap-1.5 text-xs text-emerald-300" data-testid="verify-confirmed"><BadgeCheck className="h-4 w-4" /> {t("verify.badge")}</p>}
          <button type="submit" className="btn-gold btn-sm" disabled={busy} data-testid="verify-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("reviews.save")}</button>
        </form>
      )}
    </section>
  );
};

export const VerifiedBadge = ({ testId }) => {
  const { t } = useI18n();
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[rgba(63,214,224,0.6)] px-2 py-0.5 text-[0.65rem] font-semibold text-[#7ff0f7]" data-testid={testId}>
      <BadgeCheck className="h-3.5 w-3.5" /> {t("verify.badge")}
    </span>
  );
};
